// usePlaybackMap.js - Custom hook for RTLS position playback animation
//
// Responsibilities
// ────────────────
// 1. Accept filter values from rtlsFilter.js (via rtlsMap.js handleRtlsFilterApply)
//    Playback filter shape: { datetimeTs, endDatetimeTs }
//    Object name is supplied separately via the `objectName` argument to apply()
// 2. Resolve the object name → single numeric Kinexon object ID via window.Riot.Api
// 3. Fetch dense position history via dataReceiver.js → APIM → Azure Function → RTLS API
// 4. Animate a Riot.Map.Cylinder disc (flat circle) frame-by-frame on the live map
// 5. Expose playback controls: play, pause, stop, seek (frameIndex), speed multiplier
// 6. Clean up the shape and cancel any in-flight animation on clear/unmount
//
// Data flow
// ─────────
// rtlsFilter.js → rtlsMap.js → usePlaybackMap.apply(filters, objectName)
//   → resolveObjectId()      [window.Riot.Api - client-side only]
//   → fetchPlaybackMap()     [dataReceiver.js → APIM → dataServe.js → RTLS API]
//   → animate Riot.Map.Cylinder across fetched [x, y] positions
//
// Playback model
// ──────────────
// Frames are the sorted [ts, objectId, x, y, z] rows returned by the backend.
// A requestAnimationFrame loop advances through frames at a rate governed by:
//   wallClockElapsedMs * speedMultiplier → maps to position-history time elapsed
// The marker shape is repositioned each frame by updating its `shift` property,
// which the Riot.Map SDK applies immediately without re-creating the shape.
//
// Shape strategy
// ──────────────
// A single Riot.Map.Cylinder with equal top/bottom (flat disc) represents the
// tracked object. It is created once when playback starts and its position is
// updated each frame via shape.shift = [x, y, 0], where x/y are world-space
// metre coordinates from the position history API.
//
// Coordinate system
// ─────────────────
// x and y from the RTLS API are world-space metre coordinates.
// No origin offset is needed - they are passed directly to shape constructors.
//
// Confirmed column layout from posHistoryApi.txt:
//   columns: ["timestamp", "object_id", "x", "y", "z"]
//   data:    [[ts, objectId, x, y, z], ...]
//
// Play/pause model
// ────────────────
// apply() fetches data, draws the marker at frame 0, then STOPS.
// The user explicitly clicks Play to start the animation.
// This avoids the rAF anchor corruption that occurs when auto-starting
// inside an async .then() chain (where the finally() re-render fires
// and corrupts rafStartWallRef before tick() uses it).

import { useState, useCallback, useRef, useEffect } from "react";
import { fetchPlaybackMap, DataReceiverError } from "../../utils/dataReceiver";

// ─── Shape constants ──────────────────────────────────────────────────────────

const MARKER_RADIUS = 1.2; // metres
const MARKER_Z_BOTTOM = 0;
const MARKER_Z_TOP = 0.1; // minimal height — Cylinder must not be zero-volume
const MARKER_COLOR = [0, 180, 255, 0.85]; // bright cyan, slightly transparent

// Trail: last N positions drawn as fading discs behind the marker
const TRAIL_LENGTH = 8;
const TRAIL_COLOR_BASE = [0, 140, 220]; // RGB — alpha calculated per disc

// Speed = position-history seconds consumed per wall-clock second.
// At 60fps with 1s sampling period: need speed >= 60 to advance 1 frame/tick.
// Practical range: 60× (just over real-time) → 3600× (1 hour per second).
export const PLAYBACK_SPEEDS = [30, 60, 120, 300, 600, 3600];
export const PLAYBACK_SPEED_DEFAULT = 60;

// ─── Shape removal helper ─────────────────────────────────────────────────────

function removeShape(map, shape) {
  if (!shape) return;
  try {
    shape.delete();
    return;
  } catch (_) {}
  try {
    if (map && typeof map.remove === "function") map.remove(shape);
  } catch (_) {}
}

// ─── Object ID resolution ─────────────────────────────────────────────────────
// Stays client-side: the backend has no access to the Kinexon WebSocket SDK.

/**
 * @param {string} objectName
 * @returns {Promise<number | null>}
 */
function resolveObjectId(objectName) {
  if (!objectName || !objectName.trim()) return Promise.resolve(null);
  try {
    const Riot = window.Riot;
    if (!Riot?.Api?.objectApi) return Promise.resolve(null);
    return Riot.Api.objectApi.getObjects().then(function (allObjects) {
      const q = objectName.trim().toLowerCase();
      const match = allObjects.find(
        (o) => o.name && o.name.toLowerCase() === q,
      );
      return match ? match.id : null;
    });
  } catch (err) {
    console.warn("[usePlaybackMap] resolveObjectId failed:", err);
    return Promise.resolve(null);
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePlaybackMap(riotMapRef) {
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [frameCount, setFrameCount] = useState(0);
  const [frameIndex, setFrameIndex] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [speed, setSpeedState] = useState(PLAYBACK_SPEED_DEFAULT);

  // ── Refs — all mutable state that lives inside the rAF loop ──────────────
  const framesRef = useRef([]); // [ts, objectId, x, y, z][] sorted asc
  const frameIndexRef = useRef(0); // mirrors frameIndex, readable inside rAF
  const speedRef = useRef(PLAYBACK_SPEED_DEFAULT);
  const rafRef = useRef(null); // rAF handle; non-null means loop is running
  const rafStartWallRef = useRef(null); // performance.now() anchor set by play()
  const rafStartTsRef = useRef(null); // position-history ts anchor set by play()
  const markerRef = useRef(null);
  const trailRef = useRef([]);
  const abortRef = useRef(null);

  // tickRef: always points to the latest tick function so the rAF loop never
  // calls a stale closure (standard pattern for stable rAF in React hooks).
  const tickRef = useRef(null);

  // ── removeAllShapes ───────────────────────────────────────────────────────
  const removeAllShapes = useCallback(
    function () {
      const map = riotMapRef.current;
      removeShape(map, markerRef.current);
      markerRef.current = null;
      trailRef.current.forEach((s) => removeShape(map, s));
      trailRef.current = [];
    },
    [riotMapRef],
  );

  // ── cancelRaf ────────────────────────────────────────────────────────────
  // Stops the loop and resets ALL anchors so the next play() starts clean.
  const cancelRaf = useCallback(function () {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    // Always reset anchors on cancel so play() never inherits stale timing.
    rafStartWallRef.current = null;
    rafStartTsRef.current = null;
  }, []);

  // ── positionMarker ────────────────────────────────────────────────────────
  const positionMarker = useCallback(
    function (frame) {
      if (!frame) return;
      const x = frame[2];
      const y = frame[3];

      const Riot = window.Riot;
      const map = riotMapRef.current;
      if (!map || !Riot?.Map?.Cylinder) return;

      // Create marker on first call
      if (!markerRef.current) {
        try {
          markerRef.current = new Riot.Map.Cylinder({
            center: [x, y, MARKER_Z_BOTTOM],
            radius: MARKER_RADIUS,
            top: MARKER_Z_TOP,
            bottom: MARKER_Z_BOTTOM,
            color: MARKER_COLOR,
            outline: {
              color: [255, 255, 255, 0.9],
              style: "solid",
              width: 1.5,
            },
          });
          map.add(markerRef.current);
          // Marker created at exact position — no shift needed on first call
          return;
        } catch (shapeErr) {
          console.warn("[usePlaybackMap] Failed to create marker:", shapeErr);
          return;
        }
      }

      // Reposition existing marker
      try {
        markerRef.current.center = [x, y, MARKER_Z_BOTTOM];
      } catch (err) {
        console.warn("[usePlaybackMap] Failed to reposition marker:", err);
      }

      // Trail discs
      const frames = framesRef.current;
      const currentIdx = frameIndexRef.current;

      for (let i = 0; i < TRAIL_LENGTH; i++) {
        const trailFrameIdx = currentIdx - (TRAIL_LENGTH - i);
        if (trailFrameIdx < 0) continue;
        const tf = frames[trailFrameIdx];
        if (!tf) continue;

        const alpha = ((i + 1) / TRAIL_LENGTH) * 0.5;

        if (!trailRef.current[i]) {
          try {
            const disc = new Riot.Map.Cylinder({
              center: [tf[2], tf[3], MARKER_Z_BOTTOM],
              radius: MARKER_RADIUS * 0.75,
              top: MARKER_Z_TOP,
              bottom: MARKER_Z_BOTTOM,
              color: [...TRAIL_COLOR_BASE, alpha],
            });
            map.add(disc);
            trailRef.current[i] = disc;
          } catch (_) {}
          continue;
        }

        try {
          trailRef.current[i].center = [tf[2], tf[3], MARKER_Z_BOTTOM];
          trailRef.current[i].color = [...TRAIL_COLOR_BASE, alpha];
        } catch (_) {}
      }
    },
    [riotMapRef],
  );

  // ── tick (stable function via ref — never goes stale, never needs useCallback) ──
  //
  // Defined as a ref so it can safely call itself recursively via rAF without
  // any closure staleness issues. tickFn.current is always the live function.
  const tickFn = useRef(null);
  tickFn.current = function tick(wallNow) {
    console.log(
      "[tick] fired, frames:",
      framesRef.current.length,
      "anchors:",
      rafStartWallRef.current,
      rafStartTsRef.current,
    );
    const frames = framesRef.current;

    if (!frames.length) {
      console.warn("[usePlaybackMap] tick: no frames, stopping.");
      rafRef.current = null;
      setIsPlaying(false);
      return;
    }

    if (rafStartWallRef.current === null || rafStartTsRef.current === null) {
      console.warn("[usePlaybackMap] tick: anchors null, stopping.");
      rafRef.current = null;
      setIsPlaying(false);
      return;
    }

    const wallElapsed = wallNow - rafStartWallRef.current;
    const tsElapsed = (wallElapsed / 1000) * speedRef.current;
    const targetTs = rafStartTsRef.current + tsElapsed;

    // Advance to the last frame whose timestamp <= targetTs
    let idx = frameIndexRef.current;
    while (idx < frames.length - 1 && frames[idx + 1][0] <= targetTs) {
      idx++;
    }

    const ended =
      idx >= frames.length - 1 && targetTs >= frames[frames.length - 1][0];

    frameIndexRef.current = idx;

    // Reposition marker directly here — no positionMarker() call needed
    // since all state is in refs accessible from this closure
    const frame = frames[idx];
    const x = frame[2];
    const y = frame[3];
    const map = riotMapRef.current;
    const Riot = window.Riot;

    if (map && Riot?.Map?.Cylinder) {
      if (!markerRef.current) {
        try {
          markerRef.current = new Riot.Map.Cylinder({
            center: [x, y, MARKER_Z_BOTTOM],
            radius: MARKER_RADIUS,
            top: MARKER_Z_TOP,
            bottom: MARKER_Z_BOTTOM,
            color: MARKER_COLOR,
            outline: {
              color: [255, 255, 255, 0.9],
              style: "solid",
              width: 1.5,
            },
          });
          map.add(markerRef.current);
        } catch (e) {
          console.warn("[usePlaybackMap] tick: failed to create marker", e);
        }
      } else {
        try {
          markerRef.current.center = [x, y, MARKER_Z_BOTTOM];
        } catch (e) {}
      }
    }

    setFrameIndex((prev) => (prev !== idx ? idx : prev));

    if (ended) {
      rafRef.current = null;
      rafStartWallRef.current = null;
      rafStartTsRef.current = null;
      setIsPlaying(false);
      return;
    }

    rafRef.current = requestAnimationFrame(tickFn.current);
  };

  // ── play ──────────────────────────────────────────────────────────────────
  const play = useCallback(function () {
    const frames = framesRef.current;

    if (!frames.length) {
      console.warn("[usePlaybackMap] play: no frames loaded.");
      return;
    }
    if (rafRef.current !== null) {
      console.warn("[usePlaybackMap] play: already playing.");
      return;
    }

    const startIdx =
      frameIndexRef.current >= frames.length - 1 ? 0 : frameIndexRef.current;

    frameIndexRef.current = startIdx;
    rafStartWallRef.current = performance.now();
    rafStartTsRef.current = frames[startIdx][0];

    setIsPlaying(true);
    setFrameIndex(startIdx);

    console.log(
      `[usePlaybackMap] play() — startIdx=${startIdx}, ` +
        `ts=${frames[startIdx][0]}, wall=${rafStartWallRef.current}, ` +
        `tickFn=${typeof tickFn.current}`,
    );

    rafRef.current = requestAnimationFrame(tickFn.current);
  }, []);

  // ── pause ─────────────────────────────────────────────────────────────────
  const pause = useCallback(
    function () {
      cancelRaf();
      setIsPlaying(false);
    },
    [cancelRaf],
  );

  // ── seek ──────────────────────────────────────────────────────────────────
  const seek = useCallback(
    function (targetIndex) {
      const frames = framesRef.current;
      if (!frames.length) return;

      const clamped = Math.max(0, Math.min(targetIndex, frames.length - 1));
      const wasPlaying = rafRef.current !== null;

      // Stop the loop, then reposition
      cancelRaf();
      frameIndexRef.current = clamped;
      setFrameIndex(clamped);
      positionMarker(frames[clamped]);

      // If we were playing, re-anchor and resume from the new position
      if (wasPlaying) {
        rafStartWallRef.current = performance.now();
        rafStartTsRef.current = frames[clamped][0];
        setIsPlaying(true);
        rafRef.current = requestAnimationFrame(tickFn.current);
      }
    },
    [cancelRaf, positionMarker],
  );

  // ── setSpeed ──────────────────────────────────────────────────────────────
  const setSpeed = useCallback(function (newSpeed) {
    const s = PLAYBACK_SPEEDS.includes(newSpeed)
      ? newSpeed
      : PLAYBACK_SPEED_DEFAULT;
    speedRef.current = s;
    setSpeedState(s);

    // Re-anchor from current frame so speed change takes effect immediately
    if (rafRef.current !== null) {
      const frames = framesRef.current;
      if (frames.length) {
        rafStartWallRef.current = performance.now();
        rafStartTsRef.current = frames[frameIndexRef.current][0];
      }
    }
  }, []);

  // ── clear ─────────────────────────────────────────────────────────────────
  const clear = useCallback(
    function () {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      cancelRaf();
      removeAllShapes();

      framesRef.current = [];
      frameIndexRef.current = 0;

      setIsActive(false);
      setIsPlaying(false);
      setFrameCount(0);
      setFrameIndex(0);
      setDurationMs(0);
      setError(null);

      console.log("[usePlaybackMap] clear() — shapes removed, state reset.");
    },
    [cancelRaf, removeAllShapes],
  );

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      cancelRaf();
      if (abortRef.current) abortRef.current.abort();
    };
  }, [cancelRaf]);

  // ── apply ─────────────────────────────────────────────────────────────────
  // Fetches position history, draws the marker at frame 0, then STOPS.
  // The user must click Play to start animation. This avoids rAF anchor
  // corruption from auto-starting inside an async .then() chain.
  const apply = useCallback(
    function (filters, objectName, options) {
      if (!filters) filters = {};
      if (!options) options = {};

      const map = riotMapRef.current;
      if (!map) {
        console.warn("[usePlaybackMap] apply: map not ready");
        return Promise.resolve();
      }

      const Riot = window.Riot;
      if (!Riot?.Map?.Cylinder) {
        setError(
          "RTLS SDK not ready — wait for the map to connect before starting playback.",
        );
        return Promise.resolve();
      }

      const beginTs = filters.datetimeTs;
      const endTs = filters.endDatetimeTs;

      if (!beginTs || !endTs) {
        setError("A playback start and end time are required.");
        return Promise.resolve();
      }
      if (beginTs >= endTs) {
        setError("Playback start must be earlier than end time.");
        return Promise.resolve();
      }
      if (!objectName || !objectName.trim()) {
        setError("An object name is required for playback.");
        return Promise.resolve();
      }

      const samplingPeriod =
        Number.isFinite(Number(options.samplingPeriod)) &&
        Number(options.samplingPeriod) > 0
          ? Number(options.samplingPeriod)
          : 1;

      // Clear any previous session before starting
      clear();

      setIsLoading(true);
      setError(null);

      const abortController = new AbortController();
      abortRef.current = abortController;

      return resolveObjectId(objectName)
        .then(function (objectId) {
          if (abortController.signal.aborted) return;

          if (objectId === null) {
            throw new Error(
              `No RTLS object matched "${objectName}". ` +
                "Verify the object name matches exactly in the RTLS system.",
            );
          }

          console.log(
            `[usePlaybackMap] Resolved "${objectName}" → object ID ${objectId}`,
          );
          console.log("[usePlaybackMap] Fetching playback frames via APIM…", {
            objectId,
            beginTs,
            endTs,
            samplingPeriod,
          });

          return fetchPlaybackMap(
            { objectId, beginTs, endTs, samplingPeriod },
            abortController.signal,
          );
        })
        .then(function (result) {
          if (!result || abortController.signal.aborted) return;

          const { frames, meta } = result;

          if (!frames || frames.length === 0) {
            setError(
              "No position data found for this object and time range. " +
                "Try a wider range or verify the tracker was active.",
            );
            return;
          }

          console.log(
            `[usePlaybackMap] ${frames.length} frames loaded. ` +
              `Duration: ${meta.durationMs}ms. Ready to play.`,
          );

          const currentMap = riotMapRef.current;
          if (!currentMap) {
            console.warn(
              "[usePlaybackMap] map became unavailable during fetch — aborting",
            );
            return;
          }

          // Store frames and reset to start
          framesRef.current = frames;
          frameIndexRef.current = 0;

          setIsActive(true);
          setFrameCount(frames.length);
          setFrameIndex(0);
          setDurationMs(meta.durationMs);

          // Draw marker at frame 0 so the user can see where playback will start.
          // Do NOT auto-start the rAF loop here — the user clicks Play.
          positionMarker(frames[0]);

          console.log("[usePlaybackMap] Ready. Click Play to start animation.");
        })
        .catch(function (err) {
          if (err.name === "AbortError") return;
          console.error("[usePlaybackMap] apply error:", err);
          setError(
            err instanceof DataReceiverError
              ? err.message
              : err.message || "Failed to load playback data.",
          );
          clear();
        })
        .finally(function () {
          if (abortRef.current === abortController) {
            setIsLoading(false);
            abortRef.current = null;
          }
        });
    },
    [riotMapRef, clear],
  ); // tick intentionally excluded — not called here

  return {
    isActive,
    isLoading,
    isPlaying,
    error,
    frameCount,
    frameIndex,
    durationMs,
    speed,
    apply,
    play,
    pause,
    seek,
    setSpeed,
    clear,
  };
}
